import Header from "../components/Header";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, ShieldCheck, Lock, Flame } from "lucide-react";
import { FOOTER } from "../mock";

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative bg-[#0d1a2e] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-20 right-0 w-[40rem] h-[40rem] glow-emerald pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#16d196]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#16d196]" />
            About us
          </div>
          <h1 className="mt-5 text-[44px] lg:text-[72px] font-extrabold leading-[1.02] tracking-[-0.025em] max-w-5xl">
            Britain's trusted investment hub —{" "}
            <span className="text-[#16d196]">rebuilt for 2026.</span>
          </h1>
          <p className="mt-6 text-white/70 text-[17px] lg:text-[18px] max-w-3xl leading-relaxed">
            Investment Hub UK is a specialist investment platform based in Covent
            Garden, London. We exist because UK investors deserve better than commission-led
            offers stuffed through cold-callers. We rebuilt the entire experience — vetted
            offers, transparent fees, plain-English due-diligence and a real-time investor portal.
          </p>
        </div>
      </section>

      <section className="py-8 lg:py-10 bg-white border-b border-[#0d1a2e]/8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-4 lg:gap-x-8 items-center justify-items-start lg:justify-items-center">
            {[
              { Icon: BadgeCheck, t: "FCA Aware", s: "Compliance-led ops" },
              { Icon: ShieldCheck, t: "SRA Advisors", s: "Specialist legal panel" },
              { Icon: Flame, t: "Vetted Offers", s: "<6% pass our screen" },
              { Icon: Lock, t: "GDPR & ICO", s: "Bank-level security" },
              { Icon: BadgeCheck, t: "5★ Trustpilot", s: "Rated Excellent" },
              { Icon: BadgeCheck, t: "£612m+ Placed", s: "For our investors" },
            ].map(({ Icon, t, s }) => (
              <div key={t} className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[#e3f7ee] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#16d196]" strokeWidth={2.4} />
                </div>
                <div className="leading-tight min-w-0">
                  <div className="text-[12.5px] font-bold text-[#0d1a2e] truncate">{t}</div>
                  <div className="text-[11px] text-[#0d1a2e]/55 truncate">{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#16d196]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#16d196]" />
              Our mission
            </div>
            <h2 className="mt-4 text-[32px] lg:text-[48px] font-extrabold leading-[1.05] tracking-[-0.025em] text-[#0d1a2e]">
              Make investing your capital feel{" "}
              <span className="text-[#16d196]">as clear as a private bank.</span>
            </h2>
            <div className="mt-6 space-y-5 text-[#0d1a2e]/70 text-[15.5px] leading-relaxed">
              <p>
                Investment Hub UK was founded by ex-investment-bank, fintech and
                wealth-management professionals who had grown tired of seeing brilliant
                British offers buried under glossy comparison-site clutter. Today we run
                a curated panel of 11 vetted offers spanning property, fixed income,
                equity wrappers, pensions, tax-advantaged and renewable infrastructure.
              </p>
              <p>
                Every offer goes through a four-stage screen: legals, track record,
                independent stress test and ongoing covenant monitoring. Less than 6%
                of what crosses our desk reaches the platform. The result: £612m+ of
                investor capital placed since 2019, with 98% of coupons paid on date.
              </p>
              <p>
                We're a trading style of <strong className="text-[#0d1a2e]">{FOOTER.company}</strong>,
                registered at our Covent Garden HQ. Financial promotions are approved
                by an FCA-authorised firm in line with section 21 of FSMA 2000.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#0d1a2e] text-white rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
              <div className="absolute -top-20 -right-20 w-72 h-72 glow-emerald pointer-events-none" />

              <div className="relative grid grid-cols-2 gap-6">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#16d196] font-bold">Founded</div>
                  <div className="text-[40px] font-extrabold tracking-[-0.02em] mt-1 leading-none">2019</div>
                  <div className="text-[12px] text-white/55 mt-1">Covent Garden, London</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#16d196] font-bold">Sectors</div>
                  <div className="text-[40px] font-extrabold tracking-[-0.02em] mt-1 leading-none">11</div>
                  <div className="text-[12px] text-white/55 mt-1">Gilts to growth VCTs</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#16d196] font-bold">Placed</div>
                  <div className="text-[40px] font-extrabold tracking-[-0.02em] mt-1 leading-none">£612m+</div>
                  <div className="text-[12px] text-white/55 mt-1">UK investor capital</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#16d196] font-bold">Investors</div>
                  <div className="text-[40px] font-extrabold tracking-[-0.02em] mt-1 leading-none">4,200+</div>
                  <div className="text-[12px] text-white/55 mt-1">Active on platform</div>
                </div>
              </div>

              <div className="relative mt-7 pt-6 border-t border-white/10">
                <Link to="/apply">
                  <button className="w-full inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-13 py-3.5 text-[14.5px] font-bold transition-colors">
                    Get matched in 60s
                    <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}
